import {
  Box,
  Button,
  MobileStepper,
  Stack,
  TypographyProps,
} from '@mui/material';
import ArticleCard from '../ArticleCard/ArticleCard';
import ArticleMeta from '../../types/ArticleMeta';
import { ArticleCardLocation } from '../../utilities/gtm';
import { useSnapCarousel } from 'react-snap-carousel';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

interface ArticleListProps {
  readonly articles: ArticleMeta[];
  readonly location: ArticleCardLocation;
  readonly carouselListStyle?: object;
  readonly stepperAreaStyle?: object;
  readonly headingLevel?: TypographyProps['component'];
  readonly endComponent?: React.ReactNode;
}

export default function ArticleCarousel({
  articles,
  location,
  headingLevel,
  carouselListStyle = {},
  stepperAreaStyle = {},
  endComponent,
}: ArticleListProps) {
  const {
    scrollRef,
    pages,
    activePageIndex,
    prev,
    next,
    snapPointIndexes,
    hasNextPage,
    hasPrevPage,
  } = useSnapCarousel();

  return (
    <>
      <Stack
        direction='row'
        component='ol'
        ref={scrollRef}
        sx={{
          gap: 2,
          paddingY: 0,
          listStyleType: 'none',
          overflow: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          ...carouselListStyle,
        }}
      >
        {articles.map((article, index) => {
          return (
            <Box
              key={'carousel-' + article.id}
              component='li'
              sx={{
                flexShrink: 0,
                maxWidth: '320px',
                ...(snapPointIndexes.has(index) && {
                  scrollSnapAlign: 'start',
                }),
              }}
            >
              <ArticleCard
                meta={article}
                heading={headingLevel}
                gtmData={{ index, location }}
                variant='highlight'
              />
            </Box>
          );
        })}
        {endComponent && (
          <Box
            key={'carousel-end'}
            component='li'
            sx={{
              flexShrink: 0,
              maxWidth: '320px',
              width: '100%',
              ...(snapPointIndexes.has(articles.length) && {
                scrollSnapAlign: 'start',
              }),
            }}
          >
            {endComponent}
          </Box>
        )}
      </Stack>
      <MobileStepper
        variant='dots'
        steps={pages.length}
        position='static'
        activeStep={activePageIndex}
        sx={{ padding: 0, ...stepperAreaStyle }}
        nextButton={
          <Button
            variant='text'
            endIcon={<KeyboardArrowRightIcon />}
            disabled={!hasNextPage}
            aria-disabled={!hasNextPage}
            onClick={() => next()}
          >
            Seuraava
          </Button>
        }
        backButton={
          <Button
            variant='text'
            startIcon={<KeyboardArrowLeftIcon />}
            disabled={!hasPrevPage}
            aria-disabled={!hasPrevPage}
            onClick={() => prev()}
          >
            Edellinen
          </Button>
        }
      />
    </>
  );
}
